import { z } from 'zod'

/** Plan tiers from the cheapest to the most expensive; `basic` is the plan sold as Pro. */
export const SUBSCRIPTION_TIERS = ['free', 'verein_s', 'verein_m', 'basic', 'premium'] as const
export const subscriptionTierSchema = z.enum(SUBSCRIPTION_TIERS)
export type SubscriptionTier = z.infer<typeof subscriptionTierSchema>

/** The tiers that are bought through Stripe. */
export const PAID_SUBSCRIPTION_TIERS = ['verein_s', 'verein_m', 'basic', 'premium'] as const
export const paidSubscriptionTierSchema = z.enum(PAID_SUBSCRIPTION_TIERS)
export type PaidSubscriptionTier = z.infer<typeof paidSubscriptionTierSchema>

/** A higher rank is a more expensive plan: moving up is an upgrade, moving down a downgrade. */
export const SUBSCRIPTION_TIER_RANK: Record<SubscriptionTier, number> = {
  free: 0,
  verein_s: 1,
  verein_m: 2,
  basic: 3,
  premium: 4,
}

/** Narrows any tier string to a known tier, reading an unknown one as `free`. */
export function toSubscriptionTier(value: string | null | undefined): SubscriptionTier {
  const parsed = subscriptionTierSchema.safeParse(value)
  return parsed.success ? parsed.data : 'free'
}

/**
 * Credit allowance a business is refilled to on the first of every month. It is
 * a ceiling, not an accrual: an unspent month does not carry over.
 */
export const SUBSCRIPTION_TIER_CREDITS: Record<SubscriptionTier, number> = {
  free: 1000,
  verein_s: 3000,
  verein_m: 3000,
  basic: 7000,
  premium: 12000,
}

/**
 * Stripe price lookup key of the one-time credit unit any plan may buy; a Checkout sells as many
 * units as the buyer picks credits.
 */
export const CREDIT_PACK_LOOKUP_KEY = 'credits_1000'

/** Credits one unit adds as extra credits, which every monthly refill keeps. */
export const CREDIT_PACK_CREDITS_PER_UNIT = 1000

/** The fewest credits one Checkout may buy. */
export const CREDIT_PACK_MIN_CREDITS = 1000

/** The most credits one Checkout may buy. */
export const CREDIT_PACK_MAX_CREDITS = 50000

/** The amount offered before the buyer picks another. */
export const CREDIT_PACK_DEFAULT_CREDITS = 5000

/**
 * The credits on sale: `unitAmount` is the net price of one unit of `creditsPerUnit` in cents,
 * and a buyer picks `minCredits` to `maxCredits` in steps of `stepCredits`.
 */
export const creditPackSchema = z.object({
  priceId: z.string(),
  unitAmount: z.number().int().nonnegative(),
  currency: z.string(),
  creditsPerUnit: z.number().int().positive(),
  minCredits: z.number().int().positive(),
  maxCredits: z.number().int().positive(),
  stepCredits: z.number().int().positive(),
})
export type CreditPack = z.infer<typeof creditPackSchema>

/**
 * `credits` is a whole number of units within the limits. `successUrl` should carry
 * `{CHECKOUT_SESSION_ID}`, which Stripe fills in for the return.
 */
export const createCreditPackCheckoutRequestSchema = z.object({
  businessId: z.string().uuid(),
  credits: z
    .number()
    .int()
    .min(CREDIT_PACK_MIN_CREDITS)
    .max(CREDIT_PACK_MAX_CREDITS)
    .multipleOf(CREDIT_PACK_CREDITS_PER_UNIT),
  successUrl: z.string().url(),
  cancelUrl: z.string().url(),
})
export type CreateCreditPackCheckoutRequest = z.infer<typeof createCreditPackCheckoutRequestSchema>

/** The return from a pack Checkout, naming the session Stripe put in the success URL. */
export const confirmCreditPackRequestSchema = z.object({
  businessId: z.string().uuid(),
  sessionId: z.string().regex(/^cs_\w+$/),
})

/** Whether the session is paid, and the credits it booked; repeating the call books nothing more. */
export const creditPackConfirmationSchema = z.object({
  paid: z.boolean(),
  credits: z.number().int().nonnegative(),
})
export type CreditPackConfirmation = z.infer<typeof creditPackConfirmationSchema>

/** What a plan allows; -1 marks "unlimited". The image cap applies per event and per listing. */
export const tierLimitsSchema = z.object({
  maxActiveEvents: z.number().int(),
  maxDraftEvents: z.number().int(),
  maxAdvanceDays: z.number().int(),
  maxImages: z.number().int(),
})
export type TierLimits = z.infer<typeof tierLimitsSchema>

/** The limits the backend enforces for each tier. */
export const SUBSCRIPTION_TIER_LIMITS: Record<SubscriptionTier, TierLimits> = {
  free: { maxActiveEvents: 3, maxDraftEvents: 3, maxAdvanceDays: 30, maxImages: 8 },
  verein_s: { maxActiveEvents: -1, maxDraftEvents: -1, maxAdvanceDays: -1, maxImages: 14 },
  verein_m: { maxActiveEvents: -1, maxDraftEvents: -1, maxAdvanceDays: -1, maxImages: 14 },
  basic: { maxActiveEvents: -1, maxDraftEvents: -1, maxAdvanceDays: 90, maxImages: 14 },
  premium: { maxActiveEvents: -1, maxDraftEvents: -1, maxAdvanceDays: -1, maxImages: 20 },
}

export const billingStatusSchema = z.enum(['free', 'active', 'trialing', 'payment_failed'])
export type BillingStatus = z.infer<typeof billingStatusSchema>

/** Ordered cheapest-commitment first; the UI renders plan selectors in this order. */
export const BILLING_PERIODS = ['monthly', 'quarterly', 'yearly'] as const
export const billingPeriodSchema = z.enum(BILLING_PERIODS)
export type BillingPeriod = z.infer<typeof billingPeriodSchema>

/** Charges per year for each period — used to compare plans on a common basis. */
export const BILLING_PERIODS_PER_YEAR: Record<BillingPeriod, number> = {
  monthly: 12,
  quarterly: 4,
  yearly: 1,
}

export const createCheckoutRequestSchema = z.object({
  businessId: z.string().uuid(),
  priceId: z.string().min(1),
  successUrl: z.string().url(),
  cancelUrl: z.string().url(),
})
export type CreateCheckoutRequest = z.infer<typeof createCheckoutRequestSchema>

export const grantTrialRequestSchema = z.object({
  businessId: z.string().uuid(),
  productType: paidSubscriptionTierSchema,
  durationDays: z.number().int().positive(),
})
export type GrantTrialRequest = z.infer<typeof grantTrialRequestSchema>

export const checkoutSessionResponseSchema = z.object({
  sessionId: z.string(),
  url: z.string().nullable(),
})
export type CheckoutSessionResponse = z.infer<typeof checkoutSessionResponseSchema>

export const portalSessionRequestSchema = z.object({
  returnUrl: z.string().url(),
})
export type PortalSessionRequest = z.infer<typeof portalSessionRequestSchema>

export const portalSessionResponseSchema = z.object({
  url: z.string().url(),
})
export type PortalSessionResponse = z.infer<typeof portalSessionResponseSchema>

export const stripePriceSchema = z.object({
  priceId: z.string(),
  unitAmount: z.number(),
  currency: z.string(),
  billingPeriod: billingPeriodSchema,
})

export const stripeProductSchema = z.object({
  type: z.string(),
  productName: z.string().nullable(),
  prices: z.array(stripePriceSchema),
})

export const pricingResponseSchema = z.object({
  products: z.array(stripeProductSchema),
  unavailable: z.array(z.object({ type: z.string(), reason: z.string() })),
  /** Null when Stripe has no active one-time credit unit price, which is then not offered. */
  creditPack: creditPackSchema.nullable(),
})
export type PricingResponse = z.infer<typeof pricingResponseSchema>

export const pendingChangeSchema = z.object({
  effectiveAt: z.string(),
  targetTier: subscriptionTierSchema,
  targetBillingPeriod: billingPeriodSchema.nullable(),
})
export type PendingChange = z.infer<typeof pendingChangeSchema>

export const subscriptionUsageSchema = z.object({
  activeEvents: z.number().int().nonnegative(),
  draftEvents: z.number().int().nonnegative(),
  listings: z.number().int().nonnegative(),
})
export type SubscriptionUsage = z.infer<typeof subscriptionUsageSchema>

export const subscriptionStatusResponseSchema = z.object({
  tier: subscriptionTierSchema,
  status: billingStatusSchema,
  billingPeriod: billingPeriodSchema.nullable(),
  pendingChange: pendingChangeSchema.nullable(),
  trialEndsAt: z.string().nullable(),
  limits: tierLimitsSchema,
  usage: subscriptionUsageSchema,
})
export type SubscriptionStatusResponse = z.infer<typeof subscriptionStatusResponseSchema>

/** Buying or changing the plan of one profile; a new plan may name the billing entity that pays. */
export const createProfileCheckoutRequestSchema = z.object({
  businessId: z.string().uuid(),
  priceId: z.string().min(1),
  successUrl: z.string().url(),
  cancelUrl: z.string().url(),
  billingEntityId: z.string().uuid().optional(),
})
export type CreateProfileCheckoutRequest = z.infer<typeof createProfileCheckoutRequestSchema>

export const profileBillingQuerySchema = z.object({ businessId: z.string().uuid() })

/** A profile's plan: its own when it has one, else the organization's it falls back to. */
export const profileSubscriptionStatusResponseSchema = subscriptionStatusResponseSchema.extend({
  source: z.enum(['profile', 'organization']),
  billingEntityId: z.string().uuid().nullable(),
})
export type ProfileSubscriptionStatusResponse = z.infer<
  typeof profileSubscriptionStatusResponseSchema
>

export const webhookResponseSchema = z.object({
  received: z.boolean(),
})
